/**
 * Spotlight tour definitions for driver.js.
 * Routes use :orgId placeholder — TourProvider resolves with the active org.
 *
 * Step shape:
 *   { element, popover: { title, description }, route?, optional? }
 * optional: if element missing after wait, skip to next step
 */

export const TOUR_ORDER = [
  'org-setup',
  'meeting',
  'chair-dashboard',
  'observer',
  'elections',
  'committees',
];

export const TOUR_META = {
  'org-setup': {
    id: 'org-setup',
    title: 'Organization setup',
    description: 'Home tiles and organization defaults (agenda, consent, mode).',
  },
  meeting: {
    id: 'meeting',
    title: 'Running a meeting',
    description: 'Solo or secretary-assisted setup, agenda, motions, Activity, and minutes.',
  },
  'chair-dashboard': {
    id: 'chair-dashboard',
    title: 'Chair Dashboard',
    description: 'Lean chair view: script, next step, Ready/Hold, floor, notes, and messaging.',
  },
  observer: {
    id: 'observer',
    title: 'Observer view',
    description: 'Projector display: agenda, motion progress, speakers, and QR voting.',
  },
  elections: {
    id: 'elections',
    title: 'Elections',
    description: 'Nominations, speeches, and recording ballot results.',
  },
  committees: {
    id: 'committees',
    title: 'Committees',
    description: 'Standing committees, members, and referrals.',
  },
};

export const NEXT_TOUR = {
  'org-setup': 'meeting',
  meeting: 'chair-dashboard',
  'chair-dashboard': 'observer',
  observer: 'elections',
  elections: 'committees',
  committees: null,
};

export function buildTourSteps(tourId) {
  switch (tourId) {
    case 'org-setup':
      return [
        {
          element: '[data-tour="org-home-header"]',
          route: '/orgs/:orgId',
          popover: {
            title: 'Your organization',
            description:
              'The left side of the header shows your organization name, your role, and the signed-in account. This is the hub for meetings, committees, elections, and admin tools.',
          },
        },
        {
          element: '[data-tour="tile-meeting"]',
          route: '/orgs/:orgId',
          popover: {
            title: 'Start meeting',
            description:
              'Opens the chair console. Use this when you are ready to call a meeting to order.',
          },
        },
        {
          element: '[data-tour="tile-archives"]',
          route: '/orgs/:orgId',
          optional: true,
          popover: {
            title: 'Meeting Log',
            description:
              'Past meetings and their finalized minutes. Re-open a prior session to re-export minutes or review what happened.',
          },
        },
        {
          element: '[data-tour="tile-committees"]',
          route: '/orgs/:orgId',
          optional: true,
          popover: {
            title: 'Committees',
            description: 'Manage standing committees and referrals when your role has access.',
          },
        },
        {
          element: '[data-tour="tile-elections"]',
          route: '/orgs/:orgId',
          optional: true,
          popover: {
            title: 'Elections',
            description: 'Run nominations and balloting for officers or board seats.',
          },
        },
        {
          element: '[data-tour="tile-admin"]',
          route: '/orgs/:orgId',
          optional: true,
          popover: {
            title: 'Users',
            description:
              'Not everyone in the organization needs to be a "user" here. Users are the people with login accounts — they sign in to access the consoles (Chair, Secretary, Admin, etc.). Invite logins and manage each person\'s module access from this tile.',
          },
        },
        {
          element: '[data-tour="tile-roster"]',
          route: '/orgs/:orgId',
          optional: true,
          popover: {
            title: 'Member roster',
            description:
              'These are the Members who make up the membership of the Assembly — the full roster, separate from logins. A person can be on this roster, and even selected as a committee member, without ever needing a login.',
          },
        },
        {
          element: '[data-tour="tile-settings"]',
          route: '/orgs/:orgId',
          optional: true,
          popover: {
            title: 'Organization settings',
            description:
              'Admins set the organization name, default agenda, consent calendar, and UC presets here.',
          },
        },
        {
          element: '[data-tour="settings-bylaws"]',
          route: '/orgs/:orgId/settings',
          optional: true,
          popover: {
            title: 'Bylaws & special rules',
            description:
              'Upload bylaws, special rules of order, or standing rules here. Ask the assistant can reference these during a meeting for quick lookups — uploads never change motion buttons or procedure.',
          },
        },
        {
          element: '[data-tour="settings-save"]',
          route: '/orgs/:orgId/settings',
          optional: true,
          popover: {
            title: 'Save settings',
            description: 'Persist org defaults so every chair gets the same starting point.',
          },
        },
        {
          element: '[data-tour="settings-org-name"]',
          route: '/orgs/:orgId/settings',
          optional: true,
          popover: {
            title: 'Organization name',
            description:
              'Rename the organization here. The name appears on org home, meeting headers, and the Chair Dashboard.',
          },
        },
        {
          element: '[data-tour="settings-consent"]',
          route: '/orgs/:orgId/settings',
          optional: true,
          popover: {
            title: 'Consent calendar',
            description:
              'Turn the consent calendar on or off for new meetings, and manage default consent lines when it is enabled.',
          },
        },
        {
          element: '[data-tour="settings-parliamentary-mode"]',
          route: '/orgs/:orgId/settings',
          optional: true,
          popover: {
            title: 'Parliamentary mode (coming soon)',
            description:
              'Standard RONR, small board, convention, and committee profiles are planned. This control is disabled for now — mode does not change meeting procedure yet.',
          },
        },
        {
          element: '[data-tour="settings-quorum"]',
          route: '/orgs/:orgId/settings',
          optional: true,
          popover: {
            title: 'Default quorum',
            description:
              'Set the default quorum type and requirement. Chairs can still adjust attendance for a specific meeting in the setup wizard.',
          },
        },
        {
          element: '[data-tour="settings-agenda"]',
          route: '/orgs/:orgId/settings',
          optional: true,
          popover: {
            title: 'Default order of business',
            description:
              'Edit the agenda template for new meetings. During a live session you can still change this meeting’s agenda in the meeting setup wizard.',
          },
        },
      ];
    case 'meeting':
      return [
        {
          element: '[data-tour="tile-meeting"]',
          route: '/orgs/:orgId',
          popover: {
            title: 'Launch from home',
            description: 'Start here to open the meeting module. The setup wizard appears for a new session.',
          },
        },
        {
          element: '[data-tour="meeting-wizard"]',
          route: '/orgs/:orgId/meeting',
          action: 'openMeetingWizard',
          optional: true,
          popover: {
            title: 'Meeting setup wizard',
            description:
              'Confirm attendance, quorum, and this meeting’s agenda before you begin. If the wizard is closed, use Settings → Start new meeting to see it again.',
          },
        },
        {
          element: '[data-tour="wizard-session-mode"]',
          route: '/orgs/:orgId/meeting',
          action: 'openMeetingWizard',
          optional: true,
          popover: {
            title: 'Who is driving?',
            description:
              '"I\'m chairing alone": you drive everything yourself on one device — there is no separate console vs. dashboard, just the full Chair Console. "Secretary will drive": the Secretary runs the full Chair Console (Motions, Activity, elections) on their device, while the chair\'s own device shows the lean Chair Dashboard — a script view for the mic, not the full console.',
          },
        },
        {
          element: '[data-tour="wizard-quorum"]',
          route: '/orgs/:orgId/meeting',
          action: 'openMeetingWizard',
          optional: true,
          popover: {
            title: 'Quorum',
            description: 'Set total and present members so quorum status is accurate for the session.',
          },
        },
        {
          element: '[data-tour="wizard-roll-call"]',
          route: '/orgs/:orgId/meeting',
          action: 'openMeetingWizard',
          optional: true,
          popover: {
            title: 'Roll call',
            description:
              'Optional, for this meeting only. When on, a roll call against your Member Roster sets Total/Present Members and drives quorum — independent of your organization default.',
          },
        },
        {
          element: '[data-tour="wizard-qr-voting"]',
          route: '/orgs/:orgId/meeting',
          action: 'openMeetingWizard',
          optional: true,
          popover: {
            title: 'QR device voting',
            description:
              'Optional. When enabled, Observer shows a join code so phones can cast Aye/No/Abstain when you open a remote vote.',
          },
        },
        {
          element: '[data-tour="wizard-agenda"]',
          route: '/orgs/:orgId/meeting',
          action: 'openMeetingWizard',
          optional: true,
          popover: {
            title: 'Adjust the agenda',
            description:
              'Rename, remove, or add agenda items for this meeting only. Reset from the organization default anytime. Scroll in this list to edit Call to Order through Adjournment before you begin.',
          },
        },
        {
          element: '[data-tour="wizard-begin"]',
          route: '/orgs/:orgId/meeting',
          action: 'openMeetingWizard',
          optional: true,
          popover: {
            title: 'Begin meeting',
            description: 'Closes the wizard and unlocks the full Chair Console (Driver).',
          },
        },
        {
          element: '[data-tour="driver-badge"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareAssistedSession',
          optional: true,
          popover: {
            title: 'Chair Console (Driver)',
            description:
              'This badge marks the Driver role: Motions, Active business, Activity, elections, and speaker queue. The secretary (or solo chair) advances parliamentary steps here.',
          },
        },
        {
          element: '[data-tour="launch-chair-prompt"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareAssistedSession',
          optional: true,
          popover: {
            title: 'Chair Dashboard',
            description:
              'For the lean chair script view (Say this, Ready/Hold, floor, notes), run the Chair Dashboard tutorial from Help after this tour.',
          },
        },
        {
          element: '[data-tour="launch-observer"]',
          route: '/orgs/:orgId/meeting',
          action: 'dismissMeetingWizard',
          popover: {
            title: 'Launch observer',
            description:
              'Open Observer from the device projecting to the room (extended desktop or a dedicated display). Keep the Driver console on your chair/secretary device; the room sees agenda, motions, and the voting QR.',
          },
        },
        {
          element: '[data-tour="nav-meeting"]',
          route: '/orgs/:orgId/meeting',
          action: 'dismissMeetingWizard',
          optional: true,
          popover: {
            title: 'Module navigation',
            description: 'Switch between Meeting, Committees, and Elections without leaving the session.',
          },
        },
        {
          element: '[data-tour="meeting-timer"]',
          route: '/orgs/:orgId/meeting',
          action: 'setTourAgenda',
          agendaMatch: 'Call to Order',
          popover: {
            title: 'Call to order',
            description:
              'Start the meeting timer when you call the assembly to order. That opens the session clock used in minutes.',
          },
        },
        {
          element: '[data-tour="agenda-strip"]',
          route: '/orgs/:orgId/meeting',
          action: 'setTourAgenda',
          agendaMatch: 'Call to Order',
          popover: {
            title: 'Agenda strip',
            description:
              'This strip is where you advance through the order of business. The current item is highlighted; work through topics from Call to Order to Adjournment.',
          },
        },
        {
          element: '[data-tour="agenda-complete"]',
          route: '/orgs/:orgId/meeting',
          action: 'setTourAgenda',
          agendaMatch: 'Call to Order',
          optional: true,
          popover: {
            title: 'Complete to advance',
            description:
              'Press Complete when the current item is finished. The strip advances to the next agenda item.',
          },
        },
        {
          element: '[data-tour="motion-menu"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareMeetingConsole',
          popover: {
            title: 'Motions',
            description:
              'Choose the motion that is in order. Smart sort surfaces what is typically allowed now; pick one to place it on the floor.',
          },
        },
        {
          element: '[data-tour="motion-breadcrumbs"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareMeetingConsole',
          popover: {
            title: 'Motion progress',
            description:
              'Once a motion is pending, breadcrumbs track the stage: Made → Second → Debate → Put → Vote → Announce.',
          },
        },
        {
          element: '[data-tour="active-business"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareMeetingConsole',
          popover: {
            title: 'Active business',
            description:
              'Drive the pending motion here: recognize, second, state, debate, put the question, record the tally, and announce the result.',
          },
        },
        {
          element: '[data-tour="chair-script"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareMeetingConsole',
          popover: {
            title: 'Chair script',
            description: 'Suggested language for the current parliamentary state — useful at the mic.',
          },
        },
        {
          element: '[data-tour="speakers-card"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareMeetingConsole',
          popover: {
            title: 'Speakers',
            description:
              'During debate, queue members, set the time limit, and advance or end the current speaker from here.',
          },
        },
        {
          element: '[data-tour="meeting-assistant"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareMeetingConsole',
          optional: true,
          popover: {
            title: 'Ask the assistant',
            description:
              'Opens a quick-reference lookup against your uploaded bylaws and general RONR-style practice — handy mid-meeting, and it never changes meeting buttons on its own. Your last question and answer are kept until you ask something new.',
          },
        },
        {
          element: '[data-tour="activity-feed"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareMeetingConsole',
          popover: {
            title: 'Activity',
            description:
              'Live feed of what happened this session — call to order, agenda completions, motions, and chair notes. Newest items appear at the top.',
          },
        },
        {
          element: '[data-tour="activity-chair-note"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareMeetingConsole',
          popover: {
            title: 'Chair note',
            description:
              'Add a note to the record (suggestions are offered). Activity-only notes stay in the feed; formal notes can also appear in exported minutes.',
          },
        },
        {
          element: '[data-tour="export-minutes"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareMeetingConsole',
          optional: true,
          popover: {
            title: 'Export minutes',
            description:
              'Open the menu (hamburger) and use Export minutes for PDF or Word. Activity is the live feed only — formal minutes come from this menu. Prefer adopting Adjourn to close the meeting; End live meeting in the menu is the backup that saves the Meeting Log and tears down the session.',
          },
        },
      ];
    case 'chair-dashboard':
      return [
        {
          element: '[data-tour="tile-meeting"]',
          route: '/orgs/:orgId',
          popover: {
            title: 'Open a meeting first',
            description:
              'Chair Dashboard is the lean script view used with secretary-assisted mode. Start from a meeting session.',
          },
        },
        {
          element: '[data-tour="chair-prompt-header"]',
          route: '/orgs/:orgId/meeting',
          action: 'openChairDashboardForTour',
          popover: {
            title: 'Chair Dashboard',
            description:
              'This view is for the chair’s device: organization name, timer, quorum, and role context—without the Motions list.',
          },
        },
        {
          element: '[data-tour="chair-prompt-script"]',
          route: '/orgs/:orgId/meeting',
          action: 'openChairDashboardForTour',
          popover: {
            title: 'Say this',
            description:
              'Suggested language for the current parliamentary state. It stays in sync with the Driver console as Motions or elections advance.',
          },
        },
        {
          element: '[data-tour="chair-prompt-next"]',
          route: '/orgs/:orgId/meeting',
          action: 'openChairDashboardForTour',
          popover: {
            title: 'Next step',
            description: 'A short cue for what the chair should do next at the mic.',
          },
        },
        {
          element: '[data-tour="chair-prompt-actions"]',
          route: '/orgs/:orgId/meeting',
          action: 'openChairDashboardForTour',
          popover: {
            title: 'Ready / Hold',
            description:
              'Signal Ready when you are set for the secretary to advance, or Hold if you need a pause. Take control when the chair must drive the console.',
          },
        },
        {
          element: '[data-tour="chair-prompt-speakers"]',
          route: '/orgs/:orgId/meeting',
          action: 'openChairDashboardForTour',
          optional: true,
          popover: {
            title: 'Floor and queue',
            description: 'See who has the floor and who is waiting to speak during debate.',
          },
        },
        {
          element: '[data-tour="chair-prompt-election"]',
          route: '/orgs/:orgId/meeting',
          action: 'openChairDashboardForTour',
          optional: true,
          popover: {
            title: 'Election card',
            description:
              'When an election is active, this card shows office, nominees, and chair cues for that ballot stage.',
          },
        },
        {
          element: '[data-tour="chair-dashboard-note"]',
          route: '/orgs/:orgId/meeting',
          action: 'openChairDashboardForTour',
          optional: true,
          popover: {
            title: 'Note to Activity',
            description: 'Add a chair note that appears in the session Activity feed on the Driver console.',
          },
        },
        {
          element: '[data-tour="chair-dashboard-message"]',
          route: '/orgs/:orgId/meeting',
          action: 'openChairDashboardForTour',
          optional: true,
          popover: {
            title: 'Message the secretary',
            description: 'Send a short private cue to the Driver without speaking over the assembly.',
          },
        },
        {
          element: '[data-tour="chair-dashboard-leave"]',
          route: '/orgs/:orgId/meeting',
          action: 'openChairDashboardForTour',
          optional: true,
          popover: {
            title: 'Return to org home',
            description: 'Leave the Dashboard and go back to the organization hub when the session is done on this device.',
          },
        },
      ];
    case 'observer':
      return [
        {
          element: '[data-tour="launch-observer"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareMeetingConsole',
          popover: {
            title: 'Open Observer',
            description:
              'Launch Observer opens a projector window. You can also use Menu → Observer view (this tab). This tour uses this tab so you can follow along.',
          },
        },
        {
          element: '[data-tour="observer-header"]',
          route: '/orgs/:orgId/meeting',
          action: 'openObserverView',
          popover: {
            title: 'Observer header',
            description:
              'What the room sees first: organization name, clock, attendance, meeting timer, and quorum. Keep this on the projector while you chair on your device.',
          },
        },
        {
          element: '[data-tour="observer-qr-voting"]',
          route: '/orgs/:orgId/meeting',
          action: 'openObserverView',
          popover: {
            title: 'QR device voting',
            description:
              'When QR voting is enabled, members scan this code (or type the short join code) on their phones. Votes appear when you open a remote vote from the chair console.',
          },
        },
        {
          element: '[data-tour="observer-status"]',
          route: '/orgs/:orgId/meeting',
          action: 'openObserverView',
          optional: true,
          popover: {
            title: 'Live meeting status',
            description: 'Present vs total members and the running meeting clock update as you work on the chair console.',
          },
        },
        {
          element: '[data-tour="observer-motion"]',
          route: '/orgs/:orgId/meeting',
          action: 'openObserverView',
          popover: {
            title: 'Motion on the floor',
            description:
              'Shows the pending motion text, maker, characteristics, and stage progress (Made → Second → Debate → Put → Vote → Announce) so the assembly can follow along.',
          },
        },
        {
          element: '[data-tour="observer-agenda"]',
          route: '/orgs/:orgId/meeting',
          action: 'openObserverView',
          popover: {
            title: 'Agenda',
            description: 'Current agenda item and what’s next in the order of business.',
          },
        },
        {
          element: '[data-tour="observer-speaker-timer"]',
          route: '/orgs/:orgId/meeting',
          action: 'openObserverView',
          popover: {
            title: 'Speaker timer',
            description: 'Who is recognized and how much speaking time remains. Color shifts as time runs low.',
          },
        },
        {
          element: '[data-tour="observer-speaker-queue"]',
          route: '/orgs/:orgId/meeting',
          action: 'openObserverView',
          popover: {
            title: 'Speaker queue',
            description: 'Members waiting to speak. The next speaker is highlighted at the top of the list.',
          },
        },
        {
          element: '[data-tour="observer-activity"]',
          route: '/orgs/:orgId/meeting',
          action: 'openObserverView',
          popover: {
            title: 'Recent activity',
            description:
              'A room-facing summary of recent proceedings (motions, agenda items, notes). Same story as the chair Activity feed, tuned for the display.',
          },
        },
        {
          element: '[data-tour="nav-meeting"]',
          route: '/orgs/:orgId/meeting',
          action: 'openMeetingView',
          optional: true,
          popover: {
            title: 'Back to the chair console',
            description:
              'Use the Meeting tab (or Launch observer for a separate window) when you are done previewing. The projector window can stay open while you chair.',
          },
        },
      ];
    case 'elections':
      return [
        {
          element: '[data-tour="tile-elections"]',
          route: '/orgs/:orgId',
          optional: true,
          popover: {
            title: 'Open Elections',
            description:
              'Start from this tile on org home, or use the Elections tab in the meeting header during a session.',
          },
        },
        {
          element: '[data-tour="nav-elections"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 1,
          optional: true,
          popover: {
            title: 'Elections tab',
            description: 'While in a meeting, this tab jumps straight into the elections module.',
          },
        },
        {
          element: '[data-tour="elections-chair-script"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 1,
          popover: {
            title: 'Chair script',
            description:
              'Suggested language for the current election stage (opening nominations, closing them, announcing results).',
          },
        },
        {
          element: '[data-tour="elections-office"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 1,
          popover: {
            title: 'Office / position',
            description:
              'Name the seat you are filling (President, Board Member, etc.). Required before you can start.',
          },
        },
        {
          element: '[data-tour="elections-speech-limit"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 1,
          popover: {
            title: 'Speech time limit',
            description: 'Default minutes for each candidate speech timer on the speeches stage.',
          },
        },
        {
          element: '[data-tour="elections-seats"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 1,
          popover: {
            title: 'Seats to fill',
            description:
              'Use more than 1 for multi-seat races (e.g. elect three directors). Results pick the top vote-getters.',
          },
        },
        {
          element: '[data-tour="elections-options"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 1,
          popover: {
            title: 'Ballot options',
            description:
              'Secret ballot, write-ins, and whether the Observer shows a vote breakdown after results.',
          },
        },
        {
          element: '[data-tour="elections-start"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 1,
          popover: {
            title: 'Start election',
            description:
              'Locks in setup and moves you to nominations. (The tour previews later stages without starting a real election.)',
          },
        },
        {
          element: '[data-tour="elections-breadcrumbs"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 2,
          popover: {
            title: 'Stage progress',
            description:
              'Setup → Nominations → Speeches → Voting → Results. Complete each stage before advancing.',
          },
        },
        {
          element: '[data-tour="elections-nominate-form"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 2,
          popover: {
            title: 'Take nominations',
            description:
              'Enter the nominee and who nominated them, then Add Nomination. Accept or decline each nominee on the right.',
          },
        },
        {
          element: '[data-tour="elections-nominees-list"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 2,
          popover: {
            title: 'Nominees list',
            description:
              'Pending nominees can be Accepted or Declined. Only accepted nominees move on to speeches and the ballot.',
          },
        },
        {
          element: '[data-tour="elections-close-noms"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 2,
          popover: {
            title: 'Close nominations',
            description:
              'When you have at least one accepted nominee, close nominations and begin candidate speeches.',
          },
        },
        {
          element: '[data-tour="elections-speech-timer"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 3,
          popover: {
            title: 'Speech timer',
            description:
              'Start each accepted candidate’s speech from the list. Pause/resume and advance with Next Speaker.',
          },
        },
        {
          element: '[data-tour="elections-candidates"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 3,
          popover: {
            title: 'Candidates',
            description:
              'Start Speech for each person. When everyone has spoken (or you skip remaining), Begin Voting.',
          },
        },
        {
          element: '[data-tour="elections-begin-voting"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 3,
          popover: {
            title: 'Begin voting',
            description: 'Moves to the ballot-results screen where you record tallies (or open a remote ballot).',
          },
        },
        {
          element: '[data-tour="elections-ballot"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 4,
          popover: {
            title: 'Record ballot results',
            description:
              'Enter vote totals for each official nominee. Add write-ins and invalid ballots if your rules allow.',
          },
        },
        {
          element: '[data-tour="elections-remote-ballot"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 4,
          optional: true,
          popover: {
            title: 'Remote ballot',
            description:
              'If QR voting was enabled for the meeting, open a remote ballot so phones choose a nominee, then pull those tallies in.',
          },
        },
        {
          element: '[data-tour="elections-complete"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 4,
          popover: {
            title: 'Complete election',
            description: 'Saves results and advances to the results screen to announce winners.',
          },
        },
        {
          element: '[data-tour="elections-results"]',
          route: '/orgs/:orgId/meeting',
          action: 'electionScreen',
          screen: 5,
          popover: {
            title: 'Results',
            description:
              'Winners and totals for Observer and minutes. You can end elections and return to the meeting from here.',
          },
        },
      ];
    case 'committees':
      return [
        {
          element: '[data-tour="tile-committees"]',
          route: '/orgs/:orgId',
          optional: true,
          popover: {
            title: 'Open Committees',
            description: 'From org home, or use the Committees tab in the meeting header.',
          },
        },
        {
          element: '[data-tour="nav-committees"]',
          route: '/orgs/:orgId/meeting',
          action: 'openCommitteesView',
          optional: true,
          popover: {
            title: 'Committees tab',
            description: 'Jump here during a meeting to manage referrals without leaving the session.',
          },
        },
        {
          element: '[data-tour="committees-header"]',
          route: '/orgs/:orgId/meeting',
          action: 'openCommitteesView',
          popover: {
            title: 'Committee management',
            description:
              'Standing committees store chair, members, and business referred from the floor (e.g. Commit / Refer).',
          },
        },
        {
          element: '[data-tour="committees-inbox"]',
          route: '/orgs/:orgId/meeting',
          action: 'openCommitteesView',
          optional: true,
          popover: {
            title: 'Assigned to me',
            description: 'A personal shortcut to pending referrals on any committee you sit on.',
          },
        },
        {
          element: '[data-tour="committees-new"]',
          route: '/orgs/:orgId/meeting',
          action: 'openCommitteesView',
          popover: {
            title: 'New committee',
            description: 'Opens the create form. Use this for standing or special committees.',
          },
        },
        {
          element: '[data-tour="committees-create-form"]',
          route: '/orgs/:orgId/meeting',
          action: 'openCommitteeCreate',
          popover: {
            title: 'Create form',
            description: 'Name, description, and chair identify the committee in referrals and reports.',
          },
        },
        {
          element: '[data-tour="committees-create-name"]',
          route: '/orgs/:orgId/meeting',
          action: 'openCommitteeCreate',
          popover: {
            title: 'Committee name',
            description: 'e.g. Finance Committee or Governance Committee — shown when you refer a motion.',
          },
        },
        {
          element: '[data-tour="committees-create-members"]',
          route: '/orgs/:orgId/meeting',
          action: 'openCommitteeCreate',
          popover: {
            title: 'Members',
            description: 'Add member names one at a time. You can edit the roster later from the committee card.',
          },
        },
        {
          element: '[data-tour="committees-create-submit"]',
          route: '/orgs/:orgId/meeting',
          action: 'openCommitteeCreate',
          popover: {
            title: 'Create committee',
            description: 'Saves the committee to this organization (synced when Supabase is configured).',
          },
        },
        {
          element: '[data-tour="committees-list"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareCommitteeCards',
          popover: {
            title: 'Committee cards',
            description:
              'Each card shows chair, member count, and pending referrals. Expand for quick stats; View Details for full workload.',
          },
        },
        {
          element: '[data-tour="committees-card-expand"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareCommitteeCards',
          popover: {
            title: 'Expand card',
            description: 'Reveal pending vs completed referral counts and recent referred items.',
          },
        },
        {
          element: '[data-tour="committees-view-details"]',
          route: '/orgs/:orgId/meeting',
          action: 'prepareCommitteeCards',
          popover: {
            title: 'View details',
            description:
              'Opens the detail pane: edit roster, see referred motions, and run a committee meeting notes flow.',
          },
        },
        {
          element: '[data-tour="committees-detail"]',
          route: '/orgs/:orgId/meeting',
          action: 'selectFirstCommittee',
          popover: {
            title: 'Detail pane',
            description:
              'Manage members, review referred business from the assembly, and record committee meeting notes when needed.',
          },
        },
        {
          element: '[data-tour="committees-hold-meeting"]',
          route: '/orgs/:orgId/meeting',
          action: 'selectFirstCommittee',
          optional: true,
          popover: {
            title: 'Hold committee meeting',
            description:
              'Starts a mini-session for this committee — separate from the main assembly meeting — to take attendance, keep notes, and act on its referred business.',
          },
        },
        {
          element: '[data-tour="committees-meeting-panel"]',
          route: '/orgs/:orgId/meeting',
          action: 'openCommitteeMeetingForTour',
          optional: true,
          popover: {
            title: 'Attendance, minutes, documents',
            description:
              'Mark who is present, log minute entries and discussion notes, and attach any work product for this committee meeting.',
          },
        },
        {
          element: '[data-tour="committees-meeting-disposition"]',
          route: '/orgs/:orgId/meeting',
          action: 'openCommitteeMeetingForTour',
          optional: true,
          popover: {
            title: 'Disposition & report to assembly',
            description:
              'Resolve each pending referral — Favorable, Unfavorable, or No recommendation — with optional recommendations. Save & end meeting records it for the assembly.',
          },
        },
      ];
    default:
      return [];
  }
}
